import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CustomResponseInterface } from '@common/interfaces/custom-responce.interface';
import { environment } from 'environments/environment';
import { BehaviorSubject, catchError, map, Observable, of, shareReplay, take, tap, timeout } from 'rxjs';

import { UserService } from '../../auth/services';
import { Commerce } from '../../commerce/models';
import { ExchangeRateMode } from '../../commerce/models/commerce.model';
import { ToastService } from './toast.service';

export type ExchangeRateLabel = 'BCV USD' | 'BCV EUR' | 'Personalizada';

export type RateSource = 'bcv-usd' | 'bcv-eur' | 'preferential' | 'manual';

/** Payload de la encuesta post-pago de clientes que vuelven. */
export interface ReturnedSurveyPayload {
    whyLeft: string;
    whyReturned: string;
    whyLeftFollowUp?: string | null;
    whyLeftOther?: string | null;
    whyReturnedFollowUp?: string | null;
    whyReturnedOther?: string | null;
    communicationSource: string;
}

@Injectable({
    providedIn: 'root',
})
export class CommerceService {
    API_URL = environment.API_URL;

    _commerce$: BehaviorSubject<Commerce> = new BehaviorSubject<Commerce>(null);
    exchangeRateBCV$: BehaviorSubject<number> = new BehaviorSubject<number>(null);
    exchangeRateBCVEur$: BehaviorSubject<number> = new BehaviorSubject<number>(null);
    // Tokens de versión (updateDate) crudos tal cual los devuelve la API, sin round-trip
    // por Date. El backend los usa como token de versión para validar la tasa de precio.
    exchangeRateBCVUpdateDate$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
    exchangeRateBCVEurUpdateDate$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
    exchangeRateInUse$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    public loadCommerce$: Observable<Commerce> = this.obtainCommerce().pipe(shareReplay(1));
    constructor(private http: HttpClient, private toastService: ToastService, private userService: UserService) {}

    updateCommerce$(commerce: Commerce): Observable<Commerce> {
        return this.http.put<any>(`${this.API_URL}/tenants/${commerce._id}`, commerce);
    }

    commerceReturnedSurvey$(tenantId: string, payload: ReturnedSurveyPayload): Observable<any> {
        return this.http.post<any>(`${this.API_URL}/tenants/returned-survey/${tenantId}`, payload);
    }

    updateCommerceImg$(file: FormData): Observable<Commerce> {
        return this.http.post<any>(`${this.API_URL}/cloud/upload-file`, file);
    }

    resetCommerce$(commerce: Commerce): Observable<Commerce> {
        return this.http.put<any>(`${this.API_URL}/reverse-operations/restart-tenant/${commerce.tenantId}`, {});
    }

    resetInventory$(resetInZero: boolean): Observable<Commerce> {
        return this.http.put<any>(`${this.API_URL}/reverse-operations/restart-inventory`, { resetInZero });
    }

    removeInventoryLocation$(location: string): Observable<CustomResponseInterface<any>> {
        return this.http.put<CustomResponseInterface<any>>(`${this.API_URL}/reverse-operations/remove-inventory-location`, { location });
    }

    get commerce(): Commerce {
        if (this._commerce$.value) {
            return this._commerce$.value;
        } else {
            this.loadCommerce$.pipe(take(1)).subscribe({
                next: (commerce: Commerce) => {
                    this.commerce = commerce;
                },
                error: err => {
                    this.toastService.error(err.error.title, err.error.message);
                },
            });
            return null;
        }
    }

    set commerce(commerce: Commerce) {
        this._commerce$.next(commerce);
        this.setExchangeRateInUse();
    }

    obtainCommerce(): Observable<Commerce> {
        return this.http.get<any>(`${this.API_URL}/tenants/tenantId`).pipe(map(res => res.data));
    }

    get exchangeRateBCV(): number {
        if (this.exchangeRateBCV$.value) {
            return this.exchangeRateBCV$.value;
        } else {
            this.obtainExchangeRateBCV$().then(res => {
                console.log('retornando bcv');
                return res;
            });
        }
    }

    async obtainExchangeRateBCV$(): Promise<any> {
        this.http.get(`${this.API_URL}/tenants/bcv/`).subscribe({
            next: (res: any) => {
                this.applyExchangeRateBCVResponse(res);
                return res.data;
            },
            error: () => {
                this.exchangeRateBCV$.next(null);
                this.exchangeRateBCVEur$.next(null);
                this.exchangeRateBCVUpdateDate$.next(null);
                this.exchangeRateBCVEurUpdateDate$.next(null);
                return null;
            },
        });
    }

    /**
     * Recarga la tasa BCV y la emite SIN redondear (o `null` si falla o tarda más de
     * `timeoutMs`). A diferencia de `obtainExchangeRateBCV$`, un error NO pisa los subjects:
     * la tasa en memoria es estado global (POS, facturas-ia, auth) y una recarga fallida no
     * debe dejarla en `null`. El timeout evita que quien espera la recarga (el pago mix
     * bloquea «Reportar pago») quede colgado si `/tenants/bcv/` no responde.
     */
    refreshExchangeRateBCV$(timeoutMs = 8000): Observable<number | null> {
        return this.http.get<any>(`${this.API_URL}/tenants/bcv/`).pipe(
            timeout(timeoutMs),
            map(res => {
                // Un 2xx sin tasa válida cuenta como fallo: no se toca el estado global.
                const rate = Number(res?.data);
                if (!(Number.isFinite(rate) && rate > 0)) {
                    return null;
                }
                this.applyExchangeRateBCVResponse(res);
                return rate;
            }),
            catchError(() => of(null)),
        );
    }

    /**
     * Tasa BCV oficial de un día ('YYYY-MM-DD', día de Caracas) para cotizar la mix por banco
     * con el día del pago reportado (`GET /exchange-rates/bcv/:date`, público). Emite `null` si
     * falla, tarda más de `timeoutMs` o el 2xx no trae una tasa válida. Nunca toca los subjects:
     * la tasa global sigue siendo la de hoy.
     */
    getExchangeRateBCVByDate$(date: string, timeoutMs = 8000): Observable<number | null> {
        return this.http.get<any>(`${this.API_URL}/exchange-rates/bcv/${date}`).pipe(
            timeout(timeoutMs),
            map(res => {
                const rate = Number(res?.data?.rate);
                return Number.isFinite(rate) && rate > 0 ? rate : null;
            }),
            catchError(() => of(null)),
        );
    }

    private applyExchangeRateBCVResponse(res: any): void {
        this.exchangeRateBCV$.next(res.data);
        if (res.eur != null) this.exchangeRateBCVEur$.next(res.eur);
        // Retrocompatible: si el backend aún no envía los tokens quedan en null.
        this.exchangeRateBCVUpdateDate$.next(res.updateDate ?? null);
        this.exchangeRateBCVEurUpdateDate$.next(res.eurUpdateDate ?? null);
        this.setExchangeRateInUse();
    }

    /**
     * Resolve the active exchange-rate mode for the current commerce, falling
     * back to the legacy `usePreferentialRate` boolean for tenants that have
     * not been backfilled yet.
     */
    getExchangeRateMode(commerce: Commerce = this._commerce$.value): ExchangeRateMode {
        if (!commerce) return 'usdBcv';
        if (commerce.exchangeRateMode) return commerce.exchangeRateMode;
        return commerce.usePreferentialRate ? 'preferential' : 'usdBcv';
    }

    setExchangeRateInUse() {
        if (!this._commerce$.value || !this.exchangeRateBCV$.value) return;

        const mode = this.getExchangeRateMode();
        if (mode === 'preferential') {
            this.exchangeRateInUse$.next(this._commerce$.value.preferentialRate);
        } else if (mode === 'eurBcv') {
            this.exchangeRateInUse$.next(this.exchangeRateBCVEur$.value ?? this.exchangeRateBCV$.value);
        } else {
            this.exchangeRateInUse$.next(this.exchangeRateBCV$.value);
        }
    }

    /**
     * Label for an arbitrary numeric rate, comparing it against the current
     * BCV USD / EUR streams and the tenant's preferential rate.
     */
    labelForRate(rate: number): ExchangeRateLabel {
        if (rate == null) return 'BCV USD';
        const eps = 0.01;
        const eq = (a: number, b: number) => a != null && b != null && Math.abs(a - b) < eps;
        if (eq(rate, this.exchangeRateBCVEur$.value)) return 'BCV EUR';
        if (eq(rate, this.exchangeRateBCV$.value)) return 'BCV USD';
        return 'Personalizada';
    }

    /**
     * Origen de una tasa concreta, para que el backend audite/valide la tasa de precio.
     *
     * Se ancla al MODO configurado del comercio (no infiere el origen solo por valor):
     * comparar por valor puede colisionar cuando preferentialRate == BCV USD, haciendo que
     * una tasa preferencial se etiquete como 'bcv-usd' y el backend la rechace con un
     * STALE_EXCHANGE_RATE espurio cada vez que el BCV cambie. Dentro del modo, si la tasa NO
     * coincide con la canónica del modo se trata como 'manual' (override del cajero), que el
     * backend siempre permite y no valida por token.
     */
    rateSourceFor(rate: number): RateSource {
        if (rate == null) return 'manual';
        const eps = 0.01;
        const eq = (a: number, b: number) => a != null && b != null && Math.abs(a - b) < eps;
        const mode = this.getExchangeRateMode();

        if (mode === 'preferential') {
            return eq(rate, this._commerce$.value?.preferentialRate) ? 'preferential' : 'manual';
        }

        if (mode === 'eurBcv') {
            if (eq(rate, this.exchangeRateBCVEur$.value)) return 'bcv-eur';
            // EUR sin configurar en el server: la tasa en uso es el valor USD (ver
            // setExchangeRateInUse); se envía y valida como BCV USD, nunca como EUR.
            if (this.exchangeRateBCVEur$.value == null && eq(rate, this.exchangeRateBCV$.value)) return 'bcv-usd';
            return 'manual';
        }

        // usdBcv (modo por defecto)
        return eq(rate, this.exchangeRateBCV$.value) ? 'bcv-usd' : 'manual';
    }

    /**
     * Token de versión (updateDate) a enviar para una tasa concreta. Solo aplica a BCV
     * (USD/EUR); para preferencial y manual devuelve null (el backend no lo usa ahí).
     */
    rateUpdateDateFor(rate: number): string | null {
        const source = this.rateSourceFor(rate);
        if (source === 'bcv-usd') return this.exchangeRateBCVUpdateDate$.value;
        if (source === 'bcv-eur') return this.exchangeRateBCVEurUpdateDate$.value;
        return null;
    }

    /**
     * Siembra SINCRONA de tasa+token desde el payload de un 409 STALE_EXCHANGE_RATE.
     * Solo actualiza orígenes BCV (usdBcv/eurBcv); para otros modos no siembra, así
     * rateSourceFor no reclasifica erróneamente la tasa como BCV. Evita la carrera en
     * el reintento inmediato del cajero (que si no compararía contra la BCV vieja en memoria).
     */
    applyStaleExchangeRate(data: { expectedRate: number; mode: string; updateDate: string }): void {
        if (!(data?.expectedRate > 0)) return;
        if (data.mode === 'usdBcv') {
            this.exchangeRateBCV$.next(data.expectedRate);
            this.exchangeRateBCVUpdateDate$.next(data.updateDate ?? null);
        } else if (data.mode === 'eurBcv') {
            this.exchangeRateBCVEur$.next(data.expectedRate);
            this.exchangeRateBCVEurUpdateDate$.next(data.updateDate ?? null);
        }
        this.setExchangeRateInUse();
    }

    getEmptyState$(): Observable<any> {
        return this.http.get(`${this.API_URL}/tenants/emptyState/`);
    }

    updateCashBox$(cashBox: Object): Observable<any> {
        return this.http.post<any>(`${this.API_URL}/tenants/update-cashbox`, { cashBox });
    }

    updateThermalPrinter$(thermalPrinter: boolean): Observable<any> {
        return this.http.post<any>(`${this.API_URL}/tenants/update-thermal-printer`, { thermalPrinter }).pipe(
            tap(response => {
                const commerce = this._commerce$.value;
                if (commerce) this.commerce = { ...commerce, thermalPrinter: response.thermalPrinter };

                const tenant = this.userService.tenant$.value;
                if (tenant) this.userService.tenant = { ...tenant, thermalPrinter: response.thermalPrinter };
            }),
        );
    }
}
